import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './ReportesProveedores.css';
import './BandejaSeguimiento.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const ReportesProveedores = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tablaActiva, setTablaActiva] = useState('cuentas');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const { esPresidencia, esAdministracion, usuario } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar permisos
    const allowed = esPresidencia() || esAdministracion() || usuario?.role === 'analista_administracion';
    if (!allowed) {
      navigate('/');
      return;
    }
    
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/proveedores/estadisticas/', {
        params: {
          fecha_desde: fechaDesde || undefined,
          fecha_hasta: fechaHasta || undefined
        }
      });
      setData(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching statistics', err);
      setError('Ocurrió un error al cargar las estadísticas de proveedores.');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    // Hoja 1: KPIs
    const kpiData = [
      ['KPI', 'Valor'],
      ['Total General Solicitado (Bs)', data.kpis.total_general_solicitado],
      ['Total General Pagado (Bs)', data.kpis.total_general_pagado],
      ['Proveedores con Deuda Pendiente', data.kpis.proveedores_con_deuda],
      ['Proveedores Solventes (Pagados Totales)', data.kpis.proveedores_solventes],
    ];
    const wsKPI = XLSX.utils.aoa_to_sheet(kpiData);
    XLSX.utils.book_append_sheet(wb, wsKPI, 'Resumen KPIs');

    // Hoja 2: Pagos por Proveedor
    const pagosData = data.pagos_por_proveedor.map(p => ({
      ID: p.id,
      Proveedor: p.nombre,
      RIF: p.rif,
      'Total Solicitado (Bs)': p.total_solicitado,
      'Total Pagado (Bs)': p.total_pagado,
      'Deuda Pendiente (Bs)': p.deuda_pendiente,
      'Req. Completados': p.pagos_completados,
      'Req. Pendientes': p.pagos_pendientes
    }));
    const wsPagos = XLSX.utils.json_to_sheet(pagosData);
    XLSX.utils.book_append_sheet(wb, wsPagos, 'Deudas y Pagos');

    // Hoja 3: Categorias
    const catData = data.resumen_categorias.map(c => ({
      Categoria: c.categoria,
      'Proveedor Mas Comun': c.mas_comun,
      'Cant. Mas Comun': c.cantidad_mas_comun,
      'Proveedor Menos Comun': c.menos_comun,
      'Cant. Menos Comun': c.cantidad_menos_comun
    }));
    const wsCat = XLSX.utils.json_to_sheet(catData);
    XLSX.utils.book_append_sheet(wb, wsCat, 'Categorias');

    XLSX.writeFile(wb, `Reporte_Proveedores_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportToPDF = () => {
    if (!data) return;
    const doc = new jsPDF('landscape');
    
    doc.setFontSize(18);
    doc.text('Reporte de Proveedores y Pagos', 14, 22);
    
    doc.setFontSize(11);
    doc.text(`Fecha de Reporte: ${new Date().toLocaleDateString()}`, 14, 30);
    
    autoTable(doc, {
      startY: 40,
      head: [['Resumen KPI', 'Valor']],
      body: [
        ['Total General Solicitado (Bs)', `Bs. ${data.kpis.total_general_solicitado.toLocaleString('es-VE')}`],
        ['Total General Pagado (Bs)', `Bs. ${data.kpis.total_general_pagado.toLocaleString('es-VE')}`],
        ['Proveedores con Deuda Pendiente', data.kpis.proveedores_con_deuda],
        ['Proveedores Solventes', data.kpis.proveedores_solventes],
      ],
      theme: 'grid',
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 15,
      head: [['Proveedor', 'RIF', 'Monto Solicitado (Bs)', 'Monto Pagado (Bs)', 'Deuda (Bs)', 'Estatus']],
      body: data.pagos_por_proveedor.map(p => [
        p.nombre, 
        p.rif, 
        `Bs. ${p.total_solicitado.toLocaleString('es-VE')}`, 
        `Bs. ${p.total_pagado.toLocaleString('es-VE')}`, 
        `Bs. ${p.deuda_pendiente.toLocaleString('es-VE')}`,
        p.deuda_pendiente > 0 ? 'Con Deuda' : (p.total_solicitado > 0 ? 'Solvente' : 'Sin Movimientos')
      ]),
      theme: 'striped',
    });

    doc.save(`Reporte_Proveedores_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  if (loading) return <div className="loading">Cargando Estadísticas...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!data) return null;

  // Transform data for charts
  const pieData = data.resumen_categorias.map(cat => ({
    name: cat.categoria,
    value: cat.total_categoria || (cat.cantidad_mas_comun + cat.cantidad_menos_comun) // Volumen
  })).filter(item => item.value > 0);

  const barData = data.stats_estados.map(st => ({
    name: st.estado,
    total_bs: st.total_bs,
    requerimientos: st.total_requerimientos,
    categorias: st.categorias
  })).sort((a, b) => b.total_bs - a.total_bs); // Todos los estados ordenados por monto

  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const pData = payload[0].payload;
      return (
        <div style={{ background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', padding: '12px', borderRadius: '8px', boxShadow: 'var(--shadow-sm, 0 4px 6px rgba(0,0,0,0.1))', color: 'var(--text, #333)' }}>
          <p style={{ fontWeight: 'bold', margin: '0 0 8px 0', fontSize: '14px' }}>{label}</p>
          <p style={{ margin: '0 0 8px 0', color: 'var(--primary, #6366f1)', fontWeight: '600', fontSize: '13px' }}>
            Total Aprobado Bs. {pData.total_bs.toLocaleString('es-VE', {minimumFractionDigits: 2})}
          </p>
          {pData.categorias && pData.categorias.length > 0 && (
            <div>
              <p style={{ fontWeight: '600', margin: '8px 0 4px 0', fontSize: '13px', color: 'var(--text-muted, #64748b)' }}>Categorías:</p>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', listStyleType: 'circle' }}>
                {pData.categorias.map((c, i) => (
                  <li key={i} style={{ marginBottom: '2px' }}>{c.categoria} ({c.cantidad})</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="reportes-container">
      <div className="reportes-header">
        <h2>
          📊 Panel de Estadísticas de Proveedores
        </h2>
        <div className="export-actions">
          <button className="btn btn-success" onClick={exportToExcel}>📥 Exportar Excel</button>
          <button className="btn btn-danger" onClick={exportToPDF}>📄 Exportar PDF</button>
        </div>
      </div>

      <div className="filtros-container">
        <h3>🔍 Búsqueda y Filtros</h3>
        <div className="filtros-grid">
          <div className="form-group">
            <label>Fecha Desde</label>
            <input type="date" name="fecha_desde" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Fecha Hasta</label>
            <input type="date" name="fecha_hasta" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
          </div>
        </div>

        <div className="filtros-actions" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <button className="btn btn-primary" style={{ width: '120px' }} onClick={() => { setFechaDesde(''); setFechaHasta(''); setTimeout(fetchData, 0); }}>Limpiar</button>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" style={{ width: '250px' }} onClick={fetchData}>Aplicar Filtros</button>
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-title">Total General Solicitado</span>
          <span className="kpi-value">Bs. {data.kpis.total_general_solicitado.toLocaleString('es-VE', {minimumFractionDigits: 2})}</span>
          <span className="kpi-subtitle">Monto total histórico en el sistema</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-title">Total General Pagado</span>
          <span className="kpi-value">Bs. {data.kpis.total_general_pagado.toLocaleString('es-VE', {minimumFractionDigits: 2})}</span>
          <span className="kpi-subtitle">Monto liquidado a proveedores</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-title">Proveedores en Deuda</span>
          <span className="kpi-value" style={{color: 'var(--danger-color)'}}>{data.kpis.proveedores_con_deuda}</span>
          <span className="kpi-subtitle">Con pagos parciales o pendientes</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-title">Proveedores Solventes</span>
          <span className="kpi-value" style={{color: 'var(--success-color)'}}>{data.kpis.proveedores_solventes}</span>
          <span className="kpi-subtitle">Todos sus pagos completados</span>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h3 className="chart-title">Distribución por Categorías</h3>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={90}
                fill="#8884d8"
                dataKey="value"
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip />
              <Legend payload={pieData.map((item, index) => ({
                id: item.name,
                type: 'square',
                value: `${item.name} (${item.value})`,
                color: COLORS[index % COLORS.length]
              }))} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Demanda por Estado (Bolívares)</h3>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={barData}
              margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{fontSize: 12}} />
              <YAxis 
                tickFormatter={(value) => `Bs ${(value / 1000000).toFixed(1)}M`} 
                tick={{fontSize: 12}} 
              />
              <RechartsTooltip content={<CustomBarTooltip />} />
              <Bar dataKey="total_bs" fill="#6366f1" radius={[4, 4, 0, 0]} name="Total Aprobado" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="tables-section">
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px', border: '1px solid var(--border)', borderRadius: '12px', padding: '6px' }}>
            <button
              className={`reporte-tab-btn ${tablaActiva === 'cuentas' ? 'active' : ''}`}
              onClick={() => setTablaActiva('cuentas')}
            >
              <span style={{ fontSize: '1rem' }}>🧾</span>
              <span>Cuentas de Proveedor</span>
            </button>
            <button
              className={`reporte-tab-btn ${tablaActiva === 'categorias' ? 'active' : ''}`}
              onClick={() => setTablaActiva('categorias')}
            >
              <span style={{ fontSize: '1rem' }}>🏷️</span>
              <span>Categorías de Proveedor</span>
            </button>
          </div>
        </div>

        {tablaActiva === 'cuentas' && (
          <div className="table-card-container">
            <div className="table-card-header">
              <h3>Estado de Cuenta por Proveedor</h3>
              <span className="table-count-pill">{data.pagos_por_proveedor.length} EN TOTAL</span>
            </div>
            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Proveedor</th>
                    <th style={{ textAlign: 'left' }}>RIF</th>
                    <th style={{ textAlign: 'left' }}>Total Solicitado</th>
                    <th style={{ textAlign: 'left' }}>Total Pagado</th>
                    <th style={{ textAlign: 'left' }}>Deuda Pendiente</th>
                    <th style={{ textAlign: 'left' }}>Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {data.pagos_por_proveedor.map(p => (
                    <tr key={p.id}>
                      <td><strong>{p.nombre}</strong></td>
                      <td>{p.rif}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>Bs. {p.total_solicitado.toLocaleString('es-VE', {minimumFractionDigits: 2})}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>Bs. {p.total_pagado.toLocaleString('es-VE', {minimumFractionDigits: 2})}</td>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: p.deuda_pendiente > 0 ? 700 : 400}}>
                        Bs. {p.deuda_pendiente.toLocaleString('es-VE', {minimumFractionDigits: 2})}
                      </td>
                      <td>
                        {p.deuda_pendiente > 0 ? (
                          <span className="estado-badge" style={{ backgroundColor: '#f44336', color: '#fff' }}>Con Deuda ({p.pagos_pendientes} reqs)</span>
                        ) : p.total_solicitado > 0 ? (
                          <span className="estado-badge" style={{ backgroundColor: '#4caf50', color: '#fff' }}>Solvente</span>
                        ) : (
                          <span className="estado-badge" style={{ backgroundColor: '#9e9e9e', color: '#fff' }}>Sin Movimientos</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tablaActiva === 'categorias' && (
          <div className="table-card-container">
            <div className="table-card-header">
              <h3>Frecuencia por Categorías de Proveedores</h3>
              <span className="table-count-pill">{data.resumen_categorias.length} EN TOTAL</span>
            </div>
            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Categoría</th>
                    <th style={{ textAlign: 'left' }}>Proveedor Más Común</th>
                    <th style={{ textAlign: 'left' }}>Proveedor Menos Común</th>
                  </tr>
                </thead>
                <tbody>
                  {data.resumen_categorias.map((cat, i) => (
                    <tr key={i}>
                      <td><strong>{cat.categoria}</strong></td>
                      <td>
                        {cat.mas_comun} <span style={{color: 'var(--text-muted)', fontSize: '12px'}}>({cat.cantidad_mas_comun} veces)</span>
                      </td>
                      <td>
                        {cat.menos_comun} <span style={{color: 'var(--text-muted)', fontSize: '12px'}}>({cat.cantidad_menos_comun} veces)</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportesProveedores;
